// Enhanced currency utilities with proper INR formatting and financial calculations
import { numberToWords, formatCurrency } from "./currency-utils";

/**
 * Round number to whole number (no decimals)
 * Common utility function for rounding amounts to integers
 */
export function roundToWholeNumber(amount: number | null | undefined): number {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return 0;
  }
  return Math.round(amount);
}

/**
 * Parse numeric amounts from API/form strings (handles commas, ₹ symbol, whitespace).
 */
export function parseNumericAmount(val: unknown): number {
  if (val === null || val === undefined || val === "") return 0;
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  return parseFloat(String(val).replace(/,/g, "").replace(/[₹\s]/g, "")) || 0;
}

/**
 * Enhanced currency formatter for INR with proper rounding
 */
export function formatCurrencyINR(amount: number, showSymbol = true): string {
  const rounded = Math.round(amount * 100) / 100; // Proper financial rounding
  const formatted = new Intl.NumberFormat('en-IN', {
    style: showSymbol ? 'currency' : 'decimal',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rounded);
  
  return formatted;
}

/**
 * Format currency in INR without decimal places (rounded to whole number)
 * Useful for displaying amounts that should be shown as integers
 */
export function formatCurrencyINRNoDecimals(amount: number | null | undefined, showSymbol = true): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? '₹0' : '0';
  }
  
  const rounded = roundToWholeNumber(amount);
  const formatted = new Intl.NumberFormat('en-IN', {
    style: showSymbol ? 'currency' : 'decimal',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rounded);
  
  return formatted;
}



/**
 * Calculate TDS based on nature of payment and amount
 */
export function calculateTDS(
  amount: number, 
  natureOfPayment: 'professional' | 'contractor' | 'rent' | 'interest' | 'commission' | 'other',
  panAvailable: boolean = true
): { tdsRate: number; tdsAmount: number; netAmount: number } {
  let tdsRate = 0;
  
  // Standard TDS rates (as of 2024-25)
  switch (natureOfPayment) {
    case 'professional':
      tdsRate = panAvailable ? 10 : 20;
      break;
    case 'contractor':
      tdsRate = panAvailable ? 1 : 2;
      break;
    case 'rent':
      tdsRate = panAvailable ? 10 : 20;
      break;
    case 'interest':
      tdsRate = panAvailable ? 10 : 20;
      break;
    case 'commission':
      tdsRate = panAvailable ? 5 : 20;
      break;
    default:
      tdsRate = panAvailable ? 1 : 20;
  }
  
  const tdsAmount = Math.round((amount * tdsRate / 100) * 100) / 100;
  const netAmount = amount - tdsAmount;
  
  return { tdsRate, tdsAmount, netAmount };
}

/**
 * Calculate retention amount
 */
export function calculateRetention(
  amount: number,
  retentionPercentage: number,
  retentionDuration: number // in months
): { retentionAmount: number; netAmount: number; releaseDate: string } {
  const retentionAmount = Math.round((amount * retentionPercentage / 100) * 100) / 100;
  const netAmount = amount - retentionAmount;
  
  const releaseDate = new Date();
  releaseDate.setMonth(releaseDate.getMonth() + retentionDuration);
  
  return {
    retentionAmount,
    netAmount,
    releaseDate: releaseDate.toISOString().split('T')[0]
  };
}

/**
 * Calculate early payment discount
 */
export function calculateEarlyPaymentDiscount(
  amount: number,
  discountPercentage: number,
  daysForDiscount: number,
  invoiceDate: string,
  paymentDate: string = new Date().toISOString()
): { isEligible: boolean; discountAmount: number; netAmount: number } {
  const invoiceDateTime = new Date(invoiceDate).getTime();
  const paymentDateTime = new Date(paymentDate).getTime();
  const daysDiff = Math.floor((paymentDateTime - invoiceDateTime) / (1000 * 60 * 60 * 24));
  
  const isEligible = daysDiff <= daysForDiscount;
  const discountAmount = isEligible ? Math.round((amount * discountPercentage / 100) * 100) / 100 : 0;
  const netAmount = amount - discountAmount;
  
  return { isEligible, discountAmount, netAmount };
}

/**
 * Three-way matching validation
 */
export interface ThreeWayMatchResult {
  isMatched: boolean;
  discrepancies: string[];
  tolerance: number;
  matchPercentage: number;
}

export function performThreeWayMatching(
  poAmount: number,
  invoiceAmount: number,
  receiptAmount: number,
  tolerancePercentage: number = 5
): ThreeWayMatchResult {
  const discrepancies: string[] = [];
  const tolerance = tolerancePercentage / 100;
  
  // PO vs Invoice matching
  const poInvoiceDiff = Math.abs(poAmount - invoiceAmount) / poAmount;
  if (poInvoiceDiff > tolerance) {
    discrepancies.push(`Invoice amount (${formatCurrencyINR(invoiceAmount)}) differs from PO amount (${formatCurrencyINR(poAmount)}) by ${(poInvoiceDiff * 100).toFixed(2)}%`);
  }
  
  // Invoice vs Receipt matching
  const invoiceReceiptDiff = Math.abs(invoiceAmount - receiptAmount) / invoiceAmount;
  if (invoiceReceiptDiff > tolerance) {
    discrepancies.push(`Receipt amount (${formatCurrencyINR(receiptAmount)}) differs from Invoice amount (${formatCurrencyINR(invoiceAmount)}) by ${(invoiceReceiptDiff * 100).toFixed(2)}%`);
  }
  
  // PO vs Receipt matching
  const poReceiptDiff = Math.abs(poAmount - receiptAmount) / poAmount;
  if (poReceiptDiff > tolerance) {
    discrepancies.push(`Receipt amount (${formatCurrencyINR(receiptAmount)}) differs from PO amount (${formatCurrencyINR(poAmount)}) by ${(poReceiptDiff * 100).toFixed(2)}%`);
  }
  
  const maxDiff = Math.max(poInvoiceDiff, invoiceReceiptDiff, poReceiptDiff);
  const matchPercentage = Math.max(0, (1 - maxDiff) * 100);
  const isMatched = discrepancies.length === 0;
  
  return {
    isMatched,
    discrepancies,
    tolerance: tolerancePercentage,
    matchPercentage: Math.round(matchPercentage * 100) / 100
  };
}

/**
 * Enhanced number to words for amounts in INR
 */
export function amountToWordsINR(amount: number): string {
  const wholePart = Math.floor(amount);
  const decimalPart = Math.round((amount - wholePart) * 100);
  
  let words = `${numberToWords(wholePart)} Rupees`;
  
  if (decimalPart > 0) {
    words += ` and ${numberToWords(decimalPart)} Paise`;
  }
  
  return `${words} Only`;
}

/**
 * Financial year utilities for Indian context
 */
export function getFinancialYear(date: Date = new Date()): { start: string; end: string; year: string } {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based
  
  let fyStart, fyEnd, fyYear;
  
  if (month < 3) { // Jan, Feb, Mar
    fyStart = new Date(year - 1, 3, 1); // April 1 of previous year
    fyEnd = new Date(year, 2, 31); // March 31 of current year
    fyYear = `${year - 1}-${year.toString().slice(-2)}`;
  } else { // Apr onwards
    fyStart = new Date(year, 3, 1); // April 1 of current year
    fyEnd = new Date(year + 1, 2, 31); // March 31 of next year
    fyYear = `${year}-${(year + 1).toString().slice(-2)}`;
  }
  
  return {
    start: fyStart.toISOString().split('T')[0],
    end: fyEnd.toISOString().split('T')[0],
    year: fyYear
  };
}

/**
 * Get compact INR representation:
 * >= 1 Crore  -> "1Cr", "55Cr", "150Cr"
 * >= 1 Lakh   -> "1L", "4.45L", "95.5L", "96L"
 * <  1 Lakh   -> formatted number (no suffix) using INR grouping
 */
export function getCompactINR(amount: number): string {
  if (typeof amount !== "number" || !isFinite(amount)) return String(amount);

  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);

  const formatValue = (n: number) => {
    // remove unnecessary trailing zeros: 4.50 -> 4.5, 96.00 -> 96
    const fixed = Number(n.toFixed(2));
    return fixed.toString();
  };

  const ONE_LAKH = 1e5;
  const ONE_CRORE = 1e7;

  if (abs >= ONE_CRORE) {
    const v = abs / ONE_CRORE;
    return `${sign}${formatValue(v)}Cr`;
  }

  if (abs >= ONE_LAKH) {
    const v = abs / ONE_LAKH;
    return `${sign}${formatValue(v)}L`;
  }

  // fallback: return nicely formatted number without currency symbol
  return `${sign}${formatCurrencyINR(abs, false)}`;
}